import { Component, DestroyRef, EventEmitter, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router, ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { CompanyOrderInfo } from '../../models/types/company-order-info.type';
import { UtilitiesService } from 'src/app/shared/services/utilities.service';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { AppService } from 'src/app/app.service';
import { MutationQuery } from 'src/app/models/shared/enums/mutation-query.enum';
import { parseZone } from 'moment';
import { Selectable } from 'src/app/shared/models/types/selectable.type';
import { Childable } from 'src/app/shared/models/types/childable.type';
import { CompanyStyle } from '../../models/company-style.type';
import { StyleColorShot } from '../../models/types/style-color-shot.type';
import { ModelColorSize } from 'src/app/models/photographer/create-order.model';
import { CompanyOrderDetails } from '../../models/types/company-order-details.type';
import { ShotGroup } from '../../models/types/shot-group.type';

export type UnifiedOrderOutletEvent = {
	type: string;
	payload?: unknown;
};

@Component({
	standalone: false,
	selector: 'bt-unified-order-view',
	templateUrl: './unified-order-view.component.html',
	styleUrls: ['./unified-order-view.component.scss']
})
export class UnifiedOrderViewComponent implements OnInit {
	orderId: string | number | null = null;
	activeSubRoute: string;
	isCreateMode = false;

	readonly #destroyRef = inject(DestroyRef);
	readonly #router = inject(Router);
	readonly #route = inject(ActivatedRoute);
	readonly #utilitiesService = inject(UtilitiesService);
	readonly #graphQLService = inject(GraphQLService);
	readonly #appService = inject(AppService);

	tabs = [
		{ id: 'orderInfo', title: 'Order Info', url: 'order-detail-info' },
		{ id: 'orderDetails', title: 'Order Details', url: 'order-detail-shots' }
	];

	orderInfo: CompanyOrderInfo = null;
	orderDetails: Array<Selectable<Childable<CompanyStyle, Selectable<StyleColorShot>>>> = [];
	warehouseId: number = 0;

	/** Edit mode: lista prodotti/shots da salvare (emessa dallo shots al click su Save changes). */
	orderDetailsEdit: {
		products: Array<Selectable<Childable<CompanyOrderDetails, Selectable<ShotGroup>>>>;
		orderId: number;
		warehouseId?: number;
	} | null = null;

	constructor() {
		this.#router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				takeUntilDestroyed(this.#destroyRef)
			)
			.subscribe(() => {
				this.updateRouteInfo();
			});
	}

	ngOnInit(): void {
		this.updateRouteInfo();
	}

	private updateRouteInfo(): void {
		const url = this.#router.url;
		const urlSegments = url.split('/');

		const orderViewIndex = urlSegments.indexOf('order-view');
		if (orderViewIndex !== -1 && urlSegments.length > orderViewIndex + 1) {
			const idSegment = urlSegments[orderViewIndex + 1];
			this.orderId = idSegment === 'new' ? 'new' : !isNaN(Number(idSegment)) ? Number(idSegment) : null;
			this.isCreateMode = this.orderId === 'new';

			if (urlSegments.length > orderViewIndex + 2) {
				this.activeSubRoute = urlSegments[orderViewIndex + 2];
			}
		}
	}

	onBack(): void {
		this.#router.navigate(['/company/order-list']);
	}

	selectTab(tabUrl: string): void {
		const id = this.orderId === 'new' ? 'new' : this.orderId;
		this.#router.navigate(['/company/order-view', id, tabUrl]);
	}

	onOutletActivate(component: { outletEvent?: EventEmitter<UnifiedOrderOutletEvent> }): void {
		if (component?.outletEvent) {
			component.outletEvent
				.pipe(takeUntilDestroyed(this.#destroyRef))
				.subscribe((event: UnifiedOrderOutletEvent) => this.handleOutletEvent(event));
		}
	}

	private handleOutletEvent(event: UnifiedOrderOutletEvent): void {
		switch (event.type) {
			case 'savedOrderInfo':
				this.orderInfo = event.payload as CompanyOrderInfo;
				// Navigate to shots only when creating a new order (user confirmed order info). Do not redirect when loading existing order.
				const isNewOrder = this.orderInfo?.orderId == null || this.orderInfo.orderId === 0;
				if (isNewOrder) {
					this.#router.navigate(['/company/order-view/new/order-detail-shots']);
				}
				break;
			case 'savedOrderDetails':
				this.orderDetails = (event.payload as any).styleColors as Array<Selectable<Childable<CompanyStyle, Selectable<StyleColorShot>>>>;
				this.warehouseId = (event.payload as any).warehouseId;
				this.createOrder();
				break;
			case 'savedOrderDetailsEdit': {
				const pl = event.payload as {
					products: Array<Selectable<Childable<CompanyOrderDetails, Selectable<ShotGroup>>>>;
					orderId: number;
					warehouseId?: number;
				};
				this.orderDetailsEdit = {
					products: pl.products,
					orderId: pl.orderId,
					warehouseId: pl.warehouseId
				};
				this.createOrder();
				break;
			}
			default:
				break;
		}
	}

	private createOrder(): void {
		const isEdit = this.orderDetailsEdit != null && this.orderDetailsEdit.products.length > 0;
		const data: Array<ModelColorSize> = [];

		if (isEdit) {
			const edit = this.orderDetailsEdit!;
			edit.products.forEach((detail) => {
				detail.children.forEach((group) => {
					group.shots.forEach((shot: any) => {
						data.push({
							colorId: group.colorId,
							modelId: shot.modelId,
							sizeGroupId: shot.sizeGroupId,
							shotTypeId: shot.shotTypeId,
							aspectRatioId: shot.aspectRatioId,
							mediaFormatId: shot.mediaFormatId,
							codifiedUsedId: shot.codifiedUsedId
						});
					});
				});
			});
		} else {
			this.orderDetails.forEach((s) => {
				s.children.forEach((c: StyleColorShot) =>
					c.shots.forEach((sh) => {
						data.push({
							colorId: c.colorId,
							modelId: sh.modelId,
							sizeGroupId: sh.sizeGroupId,
							shotTypeId: sh.shotTypeId,
							aspectRatioId: sh.aspectRatioId,
							mediaFormatId: sh.mediaFormatId,
							codifiedUsedId: sh.codifiedUsedId
						});
					})
				);
			});
		}

		const warehouseId = isEdit ? this.orderDetailsEdit!.warehouseId : this.warehouseId;
		const orderInfo = this.orderInfo;

		console.log({
			orderDetails: isEdit ? this.orderDetailsEdit?.products : this.orderDetails,
			orderInfo,
			warehouseId
		});

		if (data.length === 0 || !orderInfo) {
			this.#utilitiesService.showSnackbar('Missing fields');
			return;
		}

		orderInfo.dueDate = parseZone(orderInfo.dueDate).utc(true).toISOString();
		orderInfo.completitionDate = undefined;

		const companyOrderInfoInput = {
			orderId: orderInfo.orderId,
			statusId: orderInfo.statusId,
			statusName: orderInfo.statusName,
			completitionDate: orderInfo.completitionDate ?? null,
			creationDate: orderInfo.creationDate,
			dueDate: orderInfo.dueDate,
			notes: orderInfo.notes ?? '',
			photographerId: orderInfo.photographerId,
			photographerName: orderInfo.photographerName ?? ''
		};

		const payload = {
			companyOrderInfo: companyOrderInfoInput,
			stylesColorsSizes: data,
			warehouseId: warehouseId ?? 0,
			companyId: this.#appService.companyId,
			currentUserId: this.#appService.user.userId
		};

		this.#graphQLService
			.postData(MutationQuery.UPSERT_ORDER, payload)
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: (value: any) => {
					this.#utilitiesService.showSnackbar(value.message);
					this.#router.navigate(['/company/order-list']);
				},
				error: (err) => {
					console.error(err);
					this.#utilitiesService.showSnackbar(err);
				}
			});
	}
}
